import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import styles from './ShareHero.module.css';
import { useData } from '@hooks/useData';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { Captions } from 'yet-another-react-lightbox/plugins';
import 'yet-another-react-lightbox/plugins/captions.css';
import { useHtmlTransformer } from '@hooks/useHtmlTransformer';
import { useGetAllSettings } from '@services/useGetAllSettings';
import { useGetAllInstagramImages } from '@services/useGetAllInstagramImages';

export const ShareHero: FC = () => {
    const location = useLocation();
    const { transformHtml } = useHtmlTransformer();
    const { request: requestAllInstagramImages } = useGetAllInstagramImages();
    const { allSettings, allInstagramImages } = useData();
    const { request: requestAllSettings } = useGetAllSettings();

    useEffect(() => {
        requestAllSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [openLightbox, setOpenLightbox] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // update images when page changes
    useEffect(() => {
        requestAllInstagramImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const slides =
        allInstagramImages?.data.map((item) => ({
            src: item.image || '',
            description: item.link ? (
                <div className={styles['lightbox-caption-link']}>
                    <Anchor
                        href={item.link}
                        mode={AnchorMode.none}
                        target="_blank"
                        rel="noopener noreferrer"
                        classes="d-flex align-items-center justify-content-center gap-2 active">
                        <>
                            <i className="fa-brands fa-instagram" aria-hidden="true" />
                            {allSettings?.data?.content_share_marketplace_title &&
                                transformHtml(allSettings?.data?.content_share_marketplace_title)}
                        </>
                    </Anchor>
                </div>
            ) : (
                ''
            ),
        })) || [];

    const handleImageClick = (index: number) => {
        setCurrentImageIndex(index);
        setOpenLightbox(true);
    };

    const titleWithoutHtml = new DOMParser().parseFromString(
        allSettings?.data?.content_share_marketplace_title || '',
        'text/html'
    );

    return (
        <section className={styles['share-hero']} data-testid="share-hero">
            <div className="px-4 py-4 py-md-5 text-center">
                <div className="py-5">
                    <Typography
                        tagVariant={TypographyTagVariants.h2}
                        classes="mb-1"
                        color="light"
                        appearanceLevel={TypographyAppearanceVariants.level2}>
                        {allSettings?.data?.content_share_marketplace_title &&
                            transformHtml(allSettings?.data?.content_share_marketplace_title)}
                    </Typography>
                    <Typography color="light" classes="m-0" fontSize="18">
                        {allSettings?.data?.content_share_marketplace_subtitle &&
                            transformHtml(allSettings?.data?.content_share_marketplace_subtitle)}
                    </Typography>
                </div>
            </div>
            <div className={styles['image-grid-container']}>
                {allInstagramImages?.data.map((item, index) => (
                    <div
                        key={`image-${index}`}
                        className={index > 2 ? `${styles['image-grid-column']} d-none d-md-block` : styles['image-grid-column']}
                        onClick={() => handleImageClick(index)}
                        style={{ cursor: 'pointer' }}>
                        <img src={item.image} alt={titleWithoutHtml.body.textContent || allSettings?.data?.marketplaceName} />
                    </div>
                ))}
            </div>

            <Lightbox
                open={openLightbox}
                close={() => setOpenLightbox(false)}
                slides={slides}
                index={currentImageIndex}
                plugins={[Captions]}
                toolbar={{
                    buttons: ['close'],
                }}
                controller={{
                    closeOnBackdropClick: true,
                }}
            />
        </section>
    );
};
